import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { TemplateSelector } from './components/TemplateSelector';
import { AssetManager, EMPTY_TEXT_FIELDS } from './components/AssetManager';
import { PreviewPanel } from './components/Preview';
import { PropertiesPanel } from './components/PropertiesPanel';
import { RenderProgressBar } from './components/RenderProgress';
import { useTemplate } from './hooks/useTemplate';
import { useCompositionProps, useCompositionMeta } from './hooks/useCompositionProps';
import { useOutputSettings } from './hooks/useOutputSettings';
import { useRender } from './hooks/useRender';
import { EditorProvider, useEditorContext } from './context/EditorContext';
import { useAssetContext } from './context/AssetContext';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useSavedProjects } from './hooks/useSavedProjects';
import { EditorCanvas } from './editor/EditorCanvas';
import { PropertiesEditor } from './editor/PropertiesEditor';
import { Toolbar } from './editor/Toolbar';
import { LayerPanel } from './editor/LayerPanel';
import { Timeline } from './editor/Timeline';
import { PlayerBackground } from './components/Preview/PlayerBackground';
import type { TextFields } from './components/AssetManager';
import type { HeadlineSegment } from './types';
import type { SavedProject } from './hooks/useSavedProjects';

// ─── Left panel — Templates / Assets / Saved tabs ─────────────

type LeftTab = 'templates' | 'assets' | 'saved';

function SavedProjectList({
  saved,
  onLoad,
  onDelete,
}: {
  saved: SavedProject[];
  onLoad: (project: SavedProject) => void;
  onDelete: (id: string) => void;
}) {
  if (saved.length === 0) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: '#475569', fontSize: 12 }}>
        No saved projects yet.{'\n'}Use the Save button (
        <svg style={{ display: 'inline', verticalAlign: 'middle' }} width="11" height="11" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V7l-4-4z" />
        </svg>
        ) in the toolbar to save layout + text + assets.
      </div>
    );
  }

  return (
    <div style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {saved.map((t) => {
        const assetEntries = Object.entries(t.assets);
        const totalAssets = assetEntries.length;
        const needsReupload = assetEntries.filter(([, a]) => a.needsReupload).length;
        return (
          <div
            key={t.id}
            style={{
              background: '#1e293b', borderRadius: 6, padding: '8px 10px',
              display: 'flex', flexDirection: 'column', gap: 4,
              border: `1px solid ${needsReupload > 0 ? '#78350f' : '#334155'}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ flex: 1, fontSize: 11, color: '#e2e8f0', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.name}
              </span>
              <button
                type="button"
                onClick={() => onLoad(t)}
                title="Load this project"
                style={{
                  padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer',
                  background: '#1d4ed8', color: '#fff', fontSize: 10, fontWeight: 600,
                }}
              >
                Load
              </button>
              <button
                type="button"
                onClick={() => onDelete(t.id)}
                title="Delete saved project"
                style={{
                  padding: '2px 6px', borderRadius: 4, border: 'none', cursor: 'pointer',
                  background: '#450a0a', color: '#fca5a5', fontSize: 10,
                }}
              >
                ✕
              </button>
            </div>
            <div style={{ fontSize: 9, color: '#475569' }}>
              {t.templateConfig.metadata.name} · {new Date(t.savedAt).toLocaleDateString()}
              {totalAssets > 0 && <span style={{ marginLeft: 6, color: '#64748b' }}>· {totalAssets} asset{totalAssets !== 1 ? 's' : ''}</span>}
            </div>
            {needsReupload > 0 && (
              <div style={{ fontSize: 9, color: '#f59e0b', background: '#451a03', borderRadius: 3, padding: '2px 5px' }}>
                ⚠ {needsReupload} media file{needsReupload !== 1 ? 's' : ''} (video/audio) need re-upload after loading
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LeftPanel({
  textFields,
  onTextChange,
  saved,
  onLoadSaved,
  onDeleteSaved,
  headlineSegments,
  subheadlineSegments,
  onAddHeadlineSegment,
  onChangeHeadlineSegment,
  onRemoveHeadlineSegment,
  onAddSubheadlineSegment,
  onChangeSubheadlineSegment,
  onRemoveSubheadlineSegment,
}: {
  textFields: TextFields;
  onTextChange: (field: keyof TextFields, value: string) => void;
  saved: SavedProject[];
  onLoadSaved: (project: SavedProject) => void;
  onDeleteSaved: (id: string) => void;
  headlineSegments: HeadlineSegment[];
  subheadlineSegments: HeadlineSegment[];
  onAddHeadlineSegment: () => void;
  onChangeHeadlineSegment: (id: string, field: keyof Omit<HeadlineSegment, 'id'>, value: string | number) => void;
  onRemoveHeadlineSegment: (id: string) => void;
  onAddSubheadlineSegment: () => void;
  onChangeSubheadlineSegment: (id: string, field: keyof Omit<HeadlineSegment, 'id'>, value: string | number) => void;
  onRemoveSubheadlineSegment: (id: string) => void;
}) {
  const [tab, setTab] = useState<LeftTab>('templates');

  return (
    <div className="flex flex-col h-full">
      <div className="flex shrink-0 border-b border-zinc-700">
        <TabBtn active={tab === 'templates'} onClick={() => setTab('templates')}>Templates</TabBtn>
        <TabBtn active={tab === 'assets'} onClick={() => setTab('assets')}>Assets</TabBtn>
        <TabBtn active={tab === 'saved'} onClick={() => setTab('saved')}>
          Saved{saved.length > 0 && <span style={{ marginLeft: 4, background: '#1d4ed8', borderRadius: 8, padding: '0 5px', fontSize: 9, color: '#fff' }}>{saved.length}</span>}
        </TabBtn>
      </div>
      <div className="flex-1 overflow-hidden">
        {tab === 'templates' && <TemplateSelector />}
        {tab === 'assets' && (
          <AssetManager
            textFields={textFields}
            onTextChange={onTextChange}
            headlineSegments={headlineSegments}
            subheadlineSegments={subheadlineSegments}
            onAddHeadlineSegment={onAddHeadlineSegment}
            onChangeHeadlineSegment={onChangeHeadlineSegment}
            onRemoveHeadlineSegment={onRemoveHeadlineSegment}
            onAddSubheadlineSegment={onAddSubheadlineSegment}
            onChangeSubheadlineSegment={onChangeSubheadlineSegment}
            onRemoveSubheadlineSegment={onRemoveSubheadlineSegment}
          />
        )}
        {tab === 'saved' && (
          <div style={{ overflowY: 'auto', height: '100%' }}>
            <SavedProjectList saved={saved} onLoad={onLoadSaved} onDelete={onDeleteSaved} />
          </div>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex-1 py-2.5 text-xs font-medium border-b-2 transition-colors',
        active
          ? 'border-blue-500 text-white bg-zinc-800/40'
          : 'border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/20',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

// ─── Right panel — Layers / Properties / Output tabs ──────────

type RightTab = 'layers' | 'props' | 'output';

function RightPanel({
  outputSettings,
  setOutputSettings,
  layers,
  layerOverrides,
  onLayerToggle,
}: {
  outputSettings: ReturnType<typeof useOutputSettings>[0];
  setOutputSettings: ReturnType<typeof useOutputSettings>[1];
  layers: import('./types').TemplateLayer[];
  layerOverrides: Record<string, boolean>;
  onLayerToggle: (id: string, visible: boolean) => void;
}) {
  const { selectedLayer, selectedTextElement, state } = useEditorContext();
  const isEditMode = state.mode === 'edit';

  const [tab, setTab] = useState<RightTab>('layers');
  const prevSelected = useRef<string | null>(null);

  // Auto-switch to props tab when a layer is selected
  useEffect(() => {
    const newId = selectedLayer?.id ?? selectedTextElement?.id ?? null;
    if (newId && newId !== prevSelected.current) {
      setTab('props');
    }
    prevSelected.current = newId;
  }, [selectedLayer, selectedTextElement]);

  const tabs: { id: RightTab; label: string }[] = isEditMode
    ? [
        { id: 'layers', label: 'Layers' },
        { id: 'props', label: 'Properties' },
        { id: 'output', label: 'Output' },
      ]
    : [{ id: 'output', label: 'Output' }];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid #3f3f46', flexShrink: 0 }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              flex: 1, padding: '9px 0', fontSize: 10, fontWeight: 700,
              border: 'none', cursor: 'pointer', background: 'transparent',
              color: tab === t.id ? '#3b82f6' : '#52525b',
              borderBottom: `2px solid ${tab === t.id ? '#3b82f6' : 'transparent'}`,
              transition: 'color 0.15s, border-color 0.15s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {isEditMode && tab === 'layers' && <LayerPanel />}
        {isEditMode && tab === 'props' && <PropertiesEditor />}
        {(!isEditMode || tab === 'output') && (
          <PropertiesPanel
            outputSettings={outputSettings}
            onOutputChange={setOutputSettings}
            layers={layers}
            layerOverrides={layerOverrides}
            onLayerToggle={onLayerToggle}
          />
        )}
      </div>
    </div>
  );
}

// ─── Inner App — must be inside EditorProvider ────────────────

function AppInner() {
  const { activeTemplate } = useTemplate();
  const { loadTemplate, state } = useEditorContext();
  useKeyboardShortcuts(); // Cmd+Z, Cmd+Shift+Z, Escape

  // Text inputs
  const [textFields, setTextFields] = useState<TextFields>(EMPTY_TEXT_FIELDS);
  const handleTextChange = useCallback(
    (field: keyof TextFields, value: string) => setTextFields((p) => ({ ...p, [field]: value })),
    []
  );

  // Headline / subheadline segments (multiple texts at different timestamps)
  const [headlineSegments, setHeadlineSegments] = useState<HeadlineSegment[]>([]);
  const [subheadlineSegments, setSubheadlineSegments] = useState<HeadlineSegment[]>([]);

  const handleAddHeadlineSegment = useCallback(() => {
    setHeadlineSegments((prev) => [
      ...prev,
      { id: `hs_${Date.now()}`, text: '', startSec: prev.length > 0 ? (prev[prev.length - 1].endSec > 0 ? prev[prev.length - 1].endSec : 0) : 0, endSec: -1 },
    ]);
  }, []);

  const handleChangeHeadlineSegment = useCallback(
    (id: string, field: keyof Omit<HeadlineSegment, 'id'>, value: string | number) => {
      setHeadlineSegments((prev) => prev.map((s) => s.id === id ? { ...s, [field]: value } : s));
    }, []
  );

  const handleRemoveHeadlineSegment = useCallback((id: string) => {
    setHeadlineSegments((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const handleAddSubheadlineSegment = useCallback(() => {
    setSubheadlineSegments((prev) => [
      ...prev,
      { id: `ss_${Date.now()}`, text: '', startSec: prev.length > 0 ? (prev[prev.length - 1].endSec > 0 ? prev[prev.length - 1].endSec : 0) : 0, endSec: -1 },
    ]);
  }, []);

  const handleChangeSubheadlineSegment = useCallback(
    (id: string, field: keyof Omit<HeadlineSegment, 'id'>, value: string | number) => {
      setSubheadlineSegments((prev) => prev.map((s) => s.id === id ? { ...s, [field]: value } : s));
    }, []
  );

  const handleRemoveSubheadlineSegment = useCallback((id: string) => {
    setSubheadlineSegments((prev) => prev.filter((s) => s.id !== id));
  }, []);

  // Output settings
  const [outputSettings, setOutputSettings] = useOutputSettings();

  // Layer visibility overrides (layerId → visible)
  const [layerOverrides, setLayerOverrides] = useState<Record<string, boolean>>({});
  const handleLayerToggle = useCallback((id: string, visible: boolean) => {
    setLayerOverrides((p) => ({ ...p, [id]: visible }));
  }, []);

  // Full project saves (localStorage)
  const { projects: saved, saveProject, deleteProject } = useSavedProjects();

  // Asset context — needed for reading current assets on save and restoring on load
  const { state: assetState, loadAssets } = useAssetContext();

  // Sync active template → EditorContext whenever it changes
  useEffect(() => {
    if (activeTemplate) {
      loadTemplate(activeTemplate);
      setLayerOverrides({});
    }
  }, [activeTemplate, loadTemplate]);

  // Use editable config from EditorContext (if available), else fall back to raw template
  const effectiveConfig = state.editableConfig ?? activeTemplate;

  // Build composition props from effective config + layer overrides
  const rawProps = useCompositionProps(textFields, outputSettings);

  const compositionProps = useMemo(() => {
    if (!rawProps) return null;
    const baseConfig = effectiveConfig ?? rawProps.templateConfig;
    const layersWithOverrides = baseConfig.layers.map((l) =>
      l.id in layerOverrides ? { ...l, visible: layerOverrides[l.id] } : l
    );
    return {
      ...rawProps,
      templateConfig: { ...baseConfig, layers: layersWithOverrides },
      headlineSegments: headlineSegments.length > 0 ? headlineSegments : undefined,
      subheadlineSegments: subheadlineSegments.length > 0 ? subheadlineSegments : undefined,
    };
  }, [rawProps, effectiveConfig, layerOverrides, headlineSegments, subheadlineSegments]);

  const meta = useCompositionMeta(outputSettings);

  // Render
  const { isRendering, progress, logs, isComplete, hasError, trigger, download, reset } = useRender();

  const handleRender = useCallback(async () => {
    if (!compositionProps) return;
    await trigger(compositionProps, outputSettings);
  }, [compositionProps, outputSettings, trigger]);

  // Save full project (layout + text + segments + assets) to localStorage
  const handleSave = useCallback(async () => {
    if (!state.editableConfig) return;
    const defaultName = `${state.editableConfig.metadata.name} — ${new Date().toLocaleDateString()}`;
    const name = window.prompt('Name for this saved project:', defaultName);
    if (name === null) return; // cancelled
    await saveProject(
      state.editableConfig,
      textFields,
      headlineSegments,
      subheadlineSegments,
      assetState.assets,
      name || defaultName,
    );
  }, [state.editableConfig, textFields, headlineSegments, subheadlineSegments, assetState.assets, saveProject]);

  const handleLoadSaved = useCallback(
    (project: SavedProject) => {
      // Restore layout
      loadTemplate(project.templateConfig);
      setLayerOverrides({});

      // Restore text fields
      setTextFields(project.textFields);

      // Restore headline/subheadline segments
      setHeadlineSegments(project.headlineSegments ?? []);
      setSubheadlineSegments(project.subheadlineSegments ?? []);

      // Restore assets (images are inline as base64; videos/audio flagged needsReupload)
      // Filter out assets that need re-upload (empty URL) so they show as empty slots
      const restorable: typeof assetState.assets = {};
      for (const [key, savedAsset] of Object.entries(project.assets)) {
        if (!savedAsset.needsReupload && savedAsset.url) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          restorable[key as any] = {
            id: savedAsset.id,
            key: savedAsset.key,
            name: savedAsset.name,
            size: savedAsset.size,
            type: savedAsset.type,
            mimeType: savedAsset.mimeType,
            url: savedAsset.url,
            thumbnailUrl: savedAsset.thumbnailUrl,
            width: savedAsset.width,
            height: savedAsset.height,
            duration: savedAsset.duration,
            uploadedAt: savedAsset.uploadedAt ?? new Date().toISOString(),
          };
        }
      }
      loadAssets(restorable);

      // Warn about assets that couldn't be persisted
      const needsReuploadCount = Object.values(project.assets).filter((a) => a.needsReupload).length;
      if (needsReuploadCount > 0) {
        const names = Object.entries(project.assets)
          .filter(([, a]) => a.needsReupload)
          .map(([, a]) => a.name)
          .join(', ');
        window.alert(`Project loaded!\n\n${needsReuploadCount} media file(s) need to be re-uploaded (video/audio can't be stored locally):\n${names}`);
      }
    },
    [loadTemplate, loadAssets]
  );

  const layers = effectiveConfig?.layers ?? [];
  const compW = meta?.width ?? 1920;
  const compH = meta?.height ?? 1080;
  const fps = meta?.fps ?? 30;
  const durationInFrames = effectiveConfig
    ? effectiveConfig.metadata.duration * fps
    : (meta?.durationInFrames ?? 1800);

  // Lightweight player — fills 100% of EditorCanvas, no controls, used as background in edit mode
  const playerSlot = compositionProps ? (
    <PlayerBackground
      compositionProps={compositionProps}
      width={compW}
      height={compH}
      durationInFrames={durationInFrames}
      fps={fps}
      livePreview={state.mode === 'edit'}
    />
  ) : null;

  return (
    <div className="h-screen w-screen flex flex-col bg-zinc-900 text-white overflow-hidden">
      {/* ── Header ──────────────────────────────────────────── */}
      <header className="h-12 shrink-0 flex items-center justify-between px-4 border-b border-zinc-700 bg-zinc-900 z-10">
        <div className="flex items-center gap-2.5">
          <div className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center shrink-0">
            <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.894L15 14M4 8h11a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V9a1 1 0 011-1z" />
            </svg>
          </div>
          <span className="text-sm font-semibold tracking-tight">AI News Renderer</span>
          <span className="text-[10px] font-mono text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">v2.0</span>
        </div>

        <div className="flex items-center gap-3">
          {effectiveConfig && (
            <span className="text-xs text-zinc-500 hidden sm:block font-mono">
              {effectiveConfig.metadata.resolution} · {effectiveConfig.metadata.duration}s · {outputSettings.fps}fps
            </span>
          )}
          <button
            type="button"
            onClick={handleRender}
            disabled={!compositionProps || isRendering}
            className={[
              'px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1.5',
              compositionProps && !isRendering
                ? 'bg-blue-600 hover:bg-blue-500 text-white cursor-pointer'
                : 'bg-zinc-700 text-zinc-500 cursor-not-allowed',
            ].join(' ')}
          >
            {isRendering ? (
              <>
                <span className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" />
                Rendering…
              </>
            ) : (
              'Render Video'
            )}
          </button>
        </div>
      </header>

      {/* ── Main workspace ───────────────────────────────────── */}
      <div className="flex flex-1 min-h-0">
        {/* Left panel */}
        <aside className="w-64 shrink-0 flex flex-col border-r border-zinc-700 bg-zinc-850 overflow-hidden">
          <LeftPanel
            textFields={textFields}
            onTextChange={handleTextChange}
            saved={saved}
            onLoadSaved={handleLoadSaved}
            onDeleteSaved={deleteProject}
            headlineSegments={headlineSegments}
            subheadlineSegments={subheadlineSegments}
            onAddHeadlineSegment={handleAddHeadlineSegment}
            onChangeHeadlineSegment={handleChangeHeadlineSegment}
            onRemoveHeadlineSegment={handleRemoveHeadlineSegment}
            onAddSubheadlineSegment={handleAddSubheadlineSegment}
            onChangeSubheadlineSegment={handleChangeSubheadlineSegment}
            onRemoveSubheadlineSegment={handleRemoveSubheadlineSegment}
          />
        </aside>

        {/* Center — Editor canvas + toolbar */}
        <main className="flex-1 min-w-0 flex flex-col min-h-0">
          {/* Toolbar */}
          {effectiveConfig && <Toolbar onSave={handleSave} />}

          {/* Canvas area: EditorCanvas in edit mode, PreviewPanel in preview mode */}
          {state.mode === 'edit' || !compositionProps ? (
            <EditorCanvas
              compWidth={compW}
              compHeight={compH}
              playerSlot={playerSlot}
              bgColor={effectiveConfig?.colorPalette.background ?? '#0a0f1e'}
              headlineText={textFields.headline}
              subheadlineText={textFields.subheadline}
            />
          ) : (
            <PreviewPanel
              compositionProps={compositionProps}
              width={compW}
              height={compH}
              durationInFrames={durationInFrames}
              fps={fps}
            />
          )}

          {/* Timeline — only in edit mode when a template is loaded */}
          {effectiveConfig && state.mode === 'edit' && (
            <Timeline fps={fps} />
          )}
        </main>

        {/* Right — Properties / Layers / Output */}
        <aside className="w-72 shrink-0 flex flex-col border-l border-zinc-700 bg-zinc-850 overflow-hidden">
          <RightPanel
            outputSettings={outputSettings}
            setOutputSettings={setOutputSettings}
            layers={layers}
            layerOverrides={layerOverrides}
            onLayerToggle={handleLayerToggle}
          />
        </aside>
      </div>

      {/* ── Bottom — Render progress ─────────────────────────── */}
      <footer className="shrink-0 bg-zinc-900">
        <RenderProgressBar
          progress={progress}
          logs={logs}
          isRendering={isRendering}
          isComplete={isComplete}
          hasError={hasError}
          onDownload={download}
          onReset={reset}
        />
      </footer>
    </div>
  );
}

// ─── Root App — wraps everything in EditorProvider ───────────

export function App() {
  return (
    <EditorProvider>
      <AppInner />
    </EditorProvider>
  );
}
